import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Input, List, ListItem, Text } from '@chakra-ui/react';
import api from '../api';

export default function ProjectSearch({ value, onSave }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [editing, setEditing] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 1) { setResults([]); setShowDropdown(false); return; }
    try {
      const { data } = await api.get(`/projects/search?q=${encodeURIComponent(q)}`);
      setResults(data);
      setShowDropdown(true);
      setHighlightIdx(-1);
    } catch (err) {
      console.error('Search error:', err);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 200);
  };

  const handleSelect = (name) => {
    setQuery('');
    setEditing(false);
    setShowDropdown(false);
    onSave(name);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && results[highlightIdx]) {
        handleSelect(results[highlightIdx]);
      } else if (query.trim()) {
        // Save as new project name
        handleSelect(query.trim());
      }
    } else if (e.key === 'Escape') {
      setEditing(false);
      setShowDropdown(false);
      setQuery('');
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (query.trim() && query.trim() !== (value || '')) {
        onSave(query.trim());
      }
      setEditing(false);
      setShowDropdown(false);
      setQuery('');
    }, 200);
  };

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
        setEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!editing) {
    return (
      <Box
        cursor="pointer"
        onClick={() => { setEditing(true); setQuery(value || ''); }}
        minH="20px"
        fontSize="12px"
        fontWeight={value ? '500' : '400'}
        color={value ? 'gray.800' : 'gray.400'}
        _hover={{ borderRadius: 'sm' }}
        px={1}
        py={0.5}
      >
        <Text noOfLines={1}>{value || 'Project name...'}</Text>
      </Box>
    );
  }

  return (
    <Box position="relative">
      <Input
        ref={inputRef}
        size="xs"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder="Search or type new..."
        variant="flushed"
        fontSize="12px"
        px={2}
        
        
        
        
        
        
      />
      {showDropdown && results.length > 0 && (
        <Box
          ref={dropdownRef}
          className="dropdown-popup"
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="white"
          border="1px"
          borderColor="gray.200"
          
          boxShadow="lg"
          zIndex={9999}
          maxH="200px"
          overflowY="auto"
        >
          <List spacing={0}>
            {results.map((name, idx) => (
              <ListItem
                key={name}
                px={3}
                py={1.5}
                fontSize="12px"
                cursor="pointer"
                bg={highlightIdx === idx ? 'blue.50' : 'white'}
                _hover={{ bg: 'blue.50' }}
                onClick={() => handleSelect(name)}
              >
                <Text fontWeight="500">{name}</Text>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      {showDropdown && results.length === 0 && query.length > 0 && (
        <Box
          ref={dropdownRef}
          className="dropdown-popup"
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="white"
          border="1px"
          borderColor="gray.200"
          
          boxShadow="lg"
          zIndex={9999}
          p={2}
        >
          <Text fontSize="11px" color="gray.500">
            Press Enter to create "{query}"
          </Text>
        </Box>
      )}
    </Box>
  );
}
